// Référentiel unique du service traiteur, partagé par le formulaire restaurateur
// (CateringServiceOfferDetail), sa liste de réservations (RestaurantCatering) et
// toutes les pages publiques, pour que libellés, listes et prix ne divergent plus.

// Format de l'offre (`service_type`) : ce que le cuisinier sert, pas l'occasion.
export const CATERING_SERVICE_TYPES = [
  { id: 'buffet', label: 'Buffet' },
  { id: 'cocktail', label: 'Cocktail' },
  { id: 'seated_dinner', label: 'Dîner assis' },
  { id: 'brunch', label: 'Brunch' },
  { id: 'bbq', label: 'Barbecue' },
  { id: 'menu', label: 'Menu dégustation' },
  { id: 'plateau', label: 'Plateau repas' },
  { id: 'catering_delivery', label: 'Livraison traiteur' },
  { id: 'other', label: 'Autre' },
];

// Anciennes valeurs de `service_type` (des occasions) encore présentes sur des offres existantes.
const LEGACY_SERVICE_TYPE_LABELS = {
  wedding: 'Mariage',
  corporate: 'Entreprise',
  birthday: 'Anniversaire',
  conference: 'Conférence',
  party: 'Fête',
};

export const getCateringServiceTypeLabel = (type) => {
  if (!type) return 'Buffet';
  const id = String(type).toLowerCase();
  const found = CATERING_SERVICE_TYPES.find((t) => t.id === id);
  return found ? found.label : LEGACY_SERVICE_TYPE_LABELS[id] || String(type);
};

// Occasion de l'événement, choisie par le client (`occasion` de la réservation).
// Les ids correspondent aux clés i18n `catering.quote.eventTypes.*`.
export const CATERING_OCCASIONS = [
  { id: 'birthday', label: 'Anniversaire' },
  { id: 'christening', label: 'Baptême' },
  { id: 'wedding', label: 'Mariage' },
  { id: 'corporate', label: 'Événement pro' },
  { id: 'other', label: 'Autre' },
];

export const getCateringOccasionLabel = (id) =>
  CATERING_OCCASIONS.find((o) => o.id === id)?.label || id || '';

// Régimes alimentaires : ceux que l'offre sait gérer (`dietary_options`, liste d'ids)
// et ceux demandés par le client (`dietary_requirements`, liste d'ids).
export const DIETARY_OPTIONS = [
  { id: 'vegetarian', label: 'Végétarien' },
  { id: 'vegan', label: 'Végan' },
  { id: 'halal', label: 'Halal' },
  { id: 'no_pork', label: 'Sans porc' },
  { id: 'gluten_free', label: 'Sans gluten' },
  { id: 'dairy_free', label: 'Sans lactose' },
  { id: 'no_peanut', label: 'Sans arachide' },
];

// Accepte la liste d'ids (format API) comme l'ancien objet { id: bool }.
export const toDietaryIds = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.keys(value).filter((k) => value[k]);
  return [];
};

export const getActiveDietaryLabels = (value) => {
  const ids = toDietaryIds(value);
  return DIETARY_OPTIONS.filter((opt) => ids.includes(opt.id)).map((opt) => opt.label);
};

// Options payantes d'une offre : drapeau de disponibilité + frais associé.
export const CATERING_PAID_OPTIONS = [
  { id: 'delivery', flag: 'delivery_available', fee: 'delivery_fee', label: 'Livraison' },
  { id: 'setup', flag: 'setup_available', fee: 'setup_fee', label: 'Installation' },
  { id: 'equipment', flag: 'equipment_rental', fee: 'equipment_fee', label: 'Matériel' },
  { id: 'staff', flag: 'staff_available', fee: 'staff_fee_per_hour', label: 'Personnel de service', perHour: true },
];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Estimation d'une demande traiteur — même calcul que l'API (compute_catering_quote) :
 * prix de base + prix/pers. × convives + options choisies ET proposées par l'offre ;
 * acompte = % de l'offre. `options` = { delivery, setup, equipment, staff_hours }.
 */
export const computeCateringQuote = (offer, guests, options = {}) => {
  const g = parseInt(guests, 10);
  if (!offer || !g || g < 1) return null;
  const base = num(offer.base_price) + num(offer.price_per_person) * g;
  let extras = 0;
  if (options.delivery && offer.delivery_available) extras += num(offer.delivery_fee);
  if (options.setup && offer.setup_available) extras += num(offer.setup_fee);
  if (options.equipment && offer.equipment_rental) extras += num(offer.equipment_fee);
  if (options.staff_hours && offer.staff_available) extras += num(offer.staff_fee_per_hour) * num(options.staff_hours);
  const total = base + extras;
  const depositPercentage = offer.deposit_percentage != null ? num(offer.deposit_percentage) : 30;
  return { base, extras, total, depositPercentage, deposit: Math.round(total * depositPercentage) / 100 };
};

// Prix d'appel d'une offre : « à partir de » = prix/pers., ou forfait si pas de prix/pers.
export const getOfferStartingPrice = (offer) =>
  num(offer?.price_per_person) > 0
    ? { amount: num(offer.price_per_person), perPerson: true }
    : { amount: num(offer?.base_price), perPerson: false };
